import { apiSlice } from './apiSlice';
import { User, LoginCredentials, RegisterData, ApiResponse, Role, Wallet } from '@/types';
import { setUser, UserResponse } from '../slices/userSlice';
import { setAvailableRoles } from '../slices/availableRolesSlice';

export type AuthRole = { name: string; id: number };

/** A login can either finish immediately or begin the MFA challenge. */
export type LoginResponse =
  | {
      success: true;
      accessToken: string;
      refreshToken?: string;
      type: string;
      role: AuthRole;
      transactionCode?: string | null;
    }
  | {
      success: true;
      mfaRequired: true;
      mfaMethod?: MfaMethod | null;
      userId: string | number;
      userType: string;
      accessToken: null;
    }
  | {
      success: true;
      mfaSetupRequired: true;
      mfaMethod?: MfaMethod | null;
      userId: string | number;
      userType: string;
      accessToken: null;
    };

export type AuthTokens = { accessToken: string; refreshToken?: string; type: string };
export type MfaMethod = 'GOOGLE' | 'TELEGRAM';

export type TelegramMfaLinkResponse = {
  linkUrl: string;
  code: string;
  expiresInSeconds: number;
};
export type TelegramMfaMutationResponse = {
  status: 'ACTIVE' | 'INACTIVE';
  method: 'telegram';
};
type MfaMethodStatus = {
  configured: boolean;
  pending: boolean;
};
export type MfaStatusResponse = {
  isMfaEnabled: boolean;
  activeMethod: MfaMethod | null;
  totp?: MfaMethodStatus | boolean;
  telegram?: MfaMethodStatus | boolean;
};
interface ChangePasswordResponse {
  status: number;
  message: string;
}
export const authApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, { username: string; password: string }>({
      query: (body) => ({
        url: '/auth/login',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    setupMfa: builder.mutation<
      { qrCode: string; secret: string } | { status: 'ACTIVE' | 'INACTIVE'; message?: string },
      | { usernameOrEmail: string; password: string; status: 'ACTIVE' }
      | { usernameOrEmail?: string; password?: string; status: 'INACTIVE'; token: string }
    >({
      query: (body) => ({ url: '/mfa/setup', method: 'POST', body }),
      invalidatesTags: ['MfaStatus', 'User'],
    }),
    confirmMfaSetup: builder.mutation<{ status: 'ACTIVE'; message?: string }, { token: string }>({
      query: (body) => ({ url: '/mfa/setup/confirm', method: 'POST', body }),
      invalidatesTags: ['MfaStatus', 'User'],
    }),
    verifyMfa: builder.mutation<
      AuthTokens,
      { username?: string; userId?: string | number; userType?: string; token: string }
    >({
      query: (body) => ({ url: '/auth/mfa/verify', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    sendLoginTelegramOtp: builder.mutation<
      { sent: true; expiresInSeconds: number },
      { userId: string | number; userType: string }
    >({
      query: (body) => ({ url: '/auth/mfa/send-otp', method: 'POST', body }),
    }),
    startTelegramLink: builder.mutation<TelegramMfaLinkResponse, void>({
      query: () => ({ url: '/mfa/telegram/link', method: 'POST' }),
      invalidatesTags: ['MfaStatus'],
    }),
    confirmTelegramLink: builder.mutation<TelegramMfaMutationResponse, { token: string }>({
      query: (body) => ({ url: '/mfa/telegram/link/confirm', method: 'POST', body }),
      invalidatesTags: ['MfaStatus', 'User'],
    }),
    sendTelegramDisableOtp: builder.mutation<{ sent: true; expiresInSeconds: number }, void>({
      query: () => ({ url: '/mfa/telegram/send-otp', method: 'POST' }),
    }),
    unlinkTelegramMfa: builder.mutation<TelegramMfaMutationResponse, { token: string }>({
      query: (body) => ({ url: '/mfa/telegram/unlink', method: 'POST', body }),
      invalidatesTags: ['MfaStatus', 'User'],
    }),
    getMfaStatus: builder.query<MfaStatusResponse, void>({
      query: () => ({ url: '/mfa/status', method: 'GET', params: { ts: Date.now() } }),
      providesTags: ['MfaStatus'],
    }),
    // new captcha endpoint
    getCaptcha: builder.query<any, void>({
      query: () => ({
        url: '/captcha',
        method: 'POST',
      }),
    }),

    logout: builder.mutation<any, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),
    getUser: builder.query<UserResponse, void>({
      query: () => ({ url: '/users/me', method: 'GET', params: { ts: Date.now() } }),

      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
        } catch (err) {
          console.error(err);
        }
      },
      providesTags: ['User'],
    }),
    availbleRoles: builder.query<Role[], void>({
      query: () => ({
        url: '/role/available',
        method: 'GET',
        params: { ts: Date.now() },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setAvailableRoles(data));
        } catch (err) {
          console.error(err);
        }
      },
      // providesTags: ['User'],
    }),
    changePassword: builder.mutation<
      { success: boolean; status?: string },
      { oldPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: '/users/me/change-password',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    OwnerChangePassword: builder.mutation<
      ChangePasswordResponse,
      { oldPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: '/admin/change-password',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),

    getWallet: builder.query<Wallet[], void>({
      query: () => ({
        url: '/users/me/wallets',
        method: 'GET',
        params: { ts: Date.now() },
      }),
      providesTags: ['Wallet'],
    }),
  }),
});

export const {
  useLoginMutation,
  useSetupMfaMutation,
  useConfirmMfaSetupMutation,
  useVerifyMfaMutation,
  useSendLoginTelegramOtpMutation,
  useStartTelegramLinkMutation,
  useConfirmTelegramLinkMutation,
  useSendTelegramDisableOtpMutation,
  useUnlinkTelegramMfaMutation,
  useGetMfaStatusQuery,
  useGetCaptchaQuery,
  useLazyGetCaptchaQuery,
  useLogoutMutation,
  useGetUserQuery,
  useChangePasswordMutation,
  useAvailbleRolesQuery,
  useGetWalletQuery,
  useOwnerChangePasswordMutation,
} = authApi;
